import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Check } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Permissions matrix — roles × modules × actions. Admin can toggle
 * individual cells; changes are saved per (role, module).
 * The `admin` role is always full-access and locked.
 */
export default function PermissionMatrix() {
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [matrix, setMatrix] = useState([]); // [{ role, modules: {module: [actions]} }]
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rbac/matrix");
      setModules(data.modules);
      setActions(data.actions);
      setMatrix(data.matrix);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleMap = useMemo(() => Object.fromEntries(matrix.map((r) => [r.role, r])), [matrix]);

  const toggle = async (role, module, action) => {
    if (role === "admin") return; // locked
    const current = new Set(roleMap[role]?.modules?.[module] || []);
    if (current.has(action)) current.delete(action); else current.add(action);
    const next = Array.from(current);
    const key = `${role}:${module}`;
    setSaving((s) => ({ ...s, [key]: true }));
    // Optimistic update
    setMatrix((m) => m.map((r) => r.role === role ? { ...r, modules: { ...r.modules, [module]: next } } : r));
    try {
      await api.post("/rbac/matrix", { role, module, actions: next });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load(); // revert on failure
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  return (
    <div data-testid="permissions-page">
      <PageHeader
        title="Permission Matrix"
        subtitle="Fine-grained RBAC per role. Admin is always full-access."
        module="Security"
      />
      <div className="p-8 overflow-x-auto">
        <div className="bg-white border border-slate-200 rounded-sm">
          <table className="w-full text-[12.5px]" data-testid="permissions-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="label-caps text-left px-4 py-2 sticky left-0 bg-slate-50 z-10 min-w-[140px]">Module</th>
                {matrix.map((r) => (
                  <th key={r.role} colSpan={actions.length} className="label-caps text-center px-4 py-2 border-l border-slate-200">
                    {r.role}
                    {r.role === "admin" && <span className="ml-1 text-[9px] text-slate-400">(locked)</span>}
                  </th>
                ))}
              </tr>
              <tr className="bg-slate-100/70">
                <th />
                {matrix.map((r) => (
                  actions.map((a) => (
                    <th key={`${r.role}-${a}`} className="text-[9px] uppercase tracking-widest font-semibold text-slate-500 px-2 py-1 text-center border-l border-slate-200 first:border-l-0">
                      {a.slice(0, 4)}
                    </th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-2 font-medium capitalize sticky left-0 bg-white z-10">{m.replace("_", " ")}</td>
                  {matrix.map((r) => (
                    actions.map((a) => {
                      const has = (roleMap[r.role]?.modules?.[m] || []).includes(a);
                      const key = `${r.role}:${m}`;
                      const locked = r.role === "admin";
                      return (
                        <td key={`${r.role}-${m}-${a}`} className="px-2 py-2 text-center border-l border-slate-200 first:border-l-0">
                          <button
                            onClick={() => toggle(r.role, m, a)}
                            disabled={locked || saving[key]}
                            data-testid={`perm-${r.role}-${m}-${a}`}
                            className={`w-6 h-6 inline-flex items-center justify-center rounded-sm border transition ${
                              has
                                ? "bg-emerald-500 border-emerald-600 text-white"
                                : "bg-white border-slate-300 text-transparent hover:border-slate-500"
                            } ${locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                            title={`${r.role}.${m}.${a}`}
                          >
                            <Check size={12} weight="bold" />
                          </button>
                        </td>
                      );
                    })
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          Actions: {actions.map((a) => <span key={a} className="mr-2"><code className="text-slate-700">{a.slice(0, 4)}</code> · {a}</span>)}
        </div>
      </div>
    </div>
  );
}
